import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { sendPasswordResetEmailUtility } from '../utils/authUtils';
import { Key, Mail, ShieldCheck, AlertCircle, Send, Check, ArrowLeft, Lock, Loader2, Sparkles } from 'lucide-react';

interface ResetPasswordViewProps {
  onBackToApp: () => void;
  initialOobCode?: string;
  initialEmail?: string;
}

export const ResetPasswordView: React.FC<ResetPasswordViewProps> = ({
  onBackToApp,
  initialOobCode = '',
  initialEmail = '',
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [oobCode, setOobCode] = useState(initialOobCode);

  const [step, setStep] = useState<'request' | 'reset_form' | 'success'>(initialOobCode ? 'reset_form' : 'request');
  const [isLoading, setIsLoading] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [codeEmail, setCodeEmail] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Parse oobCode from URL if available
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get('oobCode') || params.get('code') || '';
    if (urlCode && !oobCode) {
      setOobCode(urlCode);
      setStep('reset_form');
    }
  }, [oobCode]);

  // If oobCode is present, verify code
  useEffect(() => {
    if (oobCode && step === 'reset_form') {
      setVerifyingCode(true);
      verifyPasswordResetCode(auth, oobCode)
        .then((emailAddress) => {
          setCodeEmail(emailAddress);
          setStatusMessage({
            type: 'success',
            text: `Verification code accepted for ${emailAddress}. Enter your new password below.`,
          });
        })
        .catch((err) => {
          console.warn('Invalid or expired password reset code:', err);
          setStatusMessage({
            type: 'error',
            text: 'Password reset link has expired or has already been used. Please request a new link below.',
          });
          setStep('request');
        })
        .finally(() => {
          setVerifyingCode(false);
        });
    }
  }, [oobCode, step]);

  const handleSendResetEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsLoading(true);
    setStatusMessage(null);

    try {
      const result = await sendPasswordResetEmailUtility(email.trim());
      setStatusMessage({
        type: 'success',
        text: result.message,
      });
      setStep('success');
    } catch (err: any) {
      setStatusMessage({ type: 'error', text: err.message || 'Failed to send password reset email.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword || newPassword !== confirmPassword) {
      setStatusMessage({ type: 'error', text: 'Passwords do not match or are blank.' });
      return;
    }

    if (newPassword.length < 6) {
      setStatusMessage({ type: 'error', text: 'Password must be at least 6 characters long.' });
      return;
    }

    setIsLoading(true);
    setStatusMessage(null);

    try {
      if (oobCode) {
        await confirmPasswordReset(auth, oobCode, newPassword);
      }
      setStatusMessage({
        type: 'success',
        text: 'Your password has been successfully updated! You can now log in with your new password.',
      });
      setStep('success');
    } catch (err: any) {
      console.error('confirmPasswordReset error:', err);
      setStatusMessage({
        type: 'error',
        text: err?.message || 'Failed to update password. Code may be invalid or expired.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-indigo-950 text-white flex flex-col justify-center items-center p-4 relative overflow-hidden">
      {/* Dynamic Background Glows */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-lime-400/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Main Container Card */}
      <div className="w-full max-w-lg bg-indigo-900/90 border border-white/15 rounded-[2.5rem] p-6 sm:p-8 shadow-2xl backdrop-blur-md relative z-10 space-y-6">
        {/* Navigation Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={onBackToApp}
            className="px-3 py-1.5 bg-indigo-950 hover:bg-indigo-800 border border-white/10 rounded-xl text-xs font-bold text-indigo-200 flex items-center space-x-1.5 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to League Dashboard</span>
          </button>

          <span className="text-[10px] font-mono uppercase text-lime-400 bg-lime-400/10 px-2.5 py-1 rounded-full border border-lime-400/20">
            Official Auth Portal
          </span>
        </div>

        {/* Brand Title */}
        <div className="text-center space-y-2">
          <div className="w-14 h-14 bg-gradient-to-tr from-lime-400 to-emerald-400 text-black rounded-2xl mx-auto flex items-center justify-center font-black text-2xl shadow-xl shadow-lime-400/20">
            <Key className="w-7 h-7 stroke-[2.5]" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-black italic uppercase tracking-tight text-white">
            Reset Password
          </h1>
          <p className="text-xs text-indigo-200/80 font-medium">
            Official Password Recovery Service (<span className="text-lime-300 font-bold">support@playgroundleague.pro</span>)
          </p>
        </div>

        {/* Status Messages */}
        {statusMessage && (
          <div
            className={`p-4 rounded-2xl border text-xs font-semibold space-y-1 animate-fadeIn ${
              statusMessage.type === 'success'
                ? 'bg-lime-400/20 border-lime-400/40 text-lime-200'
                : 'bg-rose-500/20 border-rose-500/40 text-rose-200'
            }`}
          >
            <div className="flex items-center space-x-1.5 font-bold uppercase text-[10px] tracking-wider">
              {statusMessage.type === 'success' ? (
                <>
                  <ShieldCheck className="w-4 h-4 text-lime-400" />
                  <span className="text-lime-300">Confirmation Notice</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 text-rose-400" />
                  <span className="text-rose-300">Recovery Notice</span>
                </>
              )}
            </div>
            <p className="leading-relaxed">{statusMessage.text}</p>
          </div>
        )}

        {/* STEP 1: Request Password Reset Link */}
        {step === 'request' && (
          <form onSubmit={handleSendResetEmail} className="space-y-4">
            <div>
              <label className="text-xs font-bold text-indigo-200 block mb-1.5">
                Registered Athlete Email Address
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-indigo-300 absolute left-3.5 top-3.5" />
                <input
                  type="email"
                  required
                  placeholder="athlete@playgroundleague.pro"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-indigo-950 border border-white/10 rounded-2xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-lime-400 hover:bg-lime-300 disabled:opacity-50 text-black font-black italic uppercase text-xs rounded-2xl shadow-xl shadow-lime-400/20 flex items-center justify-center space-x-2 transition"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-black" />
                  <span>Dispatching Reset Email...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 stroke-[2.5]" />
                  <span>Send Reset Link via support@playgroundleague.pro</span>
                </>
              )}
            </button>
          </form>
        )}

        {/* STEP 2: Reset Form (When oobCode is present) */}
        {step === 'reset_form' && (
          <div className="space-y-4">
            {verifyingCode ? (
              <div className="p-6 bg-indigo-950 rounded-2xl border border-white/10 text-center space-y-2">
                <Loader2 className="w-8 h-8 text-lime-400 animate-spin mx-auto" />
                <p className="text-xs text-indigo-200 font-semibold">Verifying secure Firebase password reset token...</p>
              </div>
            ) : (
              <form onSubmit={handleConfirmNewPassword} className="space-y-4">
                {codeEmail && (
                  <div className="p-3 bg-indigo-950/80 rounded-xl border border-white/10 text-xs text-indigo-200">
                    Updating password for: <strong className="text-lime-300">{codeEmail}</strong>
                  </div>
                )}

                <div>
                  <label className="text-xs font-bold text-indigo-200 block mb-1">New Password</label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-indigo-300 absolute left-3.5 top-3.5" />
                    <input
                      type="password"
                      required
                      minLength={6}
                      placeholder="Minimum 6 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-indigo-950 border border-white/10 rounded-2xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-indigo-200 block mb-1">Confirm New Password</label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-indigo-300 absolute left-3.5 top-3.5" />
                    <input
                      type="password"
                      required
                      minLength={6}
                      placeholder="Re-enter password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-indigo-950 border border-white/10 rounded-2xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3.5 bg-lime-400 hover:bg-lime-300 disabled:opacity-50 text-black font-black italic uppercase text-xs rounded-2xl shadow-xl shadow-lime-400/20 flex items-center justify-center space-x-2 transition"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-black" />
                      <span>Updating Password...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 stroke-[3]" />
                      <span>Save New Password & Log In</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        )}

        {/* STEP 3: Success Screen */}
        {step === 'success' && (
          <div className="p-6 bg-lime-400/20 border border-lime-400/40 rounded-2xl text-center space-y-4">
            <ShieldCheck className="w-12 h-12 text-lime-400 mx-auto animate-bounce" />
            <div className="space-y-1">
              <h3 className="font-extrabold text-lime-300 text-base">Action Completed</h3>
              <p className="text-xs text-indigo-100 font-medium">
                Your password recovery request has been executed. Check your inbox for links from <strong className="text-lime-300">support@playgroundleague.pro</strong>.
              </p>
            </div>
            <button
              onClick={onBackToApp}
              className="w-full py-3 bg-lime-400 hover:bg-lime-300 text-black font-black uppercase text-xs rounded-xl shadow-md transition"
            >
              Return to Playground League
            </button>
          </div>
        )}

        {/* Footer Info */}
        <div className="pt-3 border-t border-white/10 flex items-center justify-between text-[11px] text-indigo-200/70">
          <span>Need direct help?</span>
          <a
            href="mailto:support@playgroundleague.pro"
            className="text-lime-400 hover:underline font-bold"
          >
            support@playgroundleague.pro
          </a>
        </div>
      </div>
    </div>
  );
};
